import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Intercom, { shutdown as shutdownIntercom, show as showIntercom } from '@intercom/messenger-js-sdk';
import { useQuery } from '@tanstack/react-query';
import { useUser } from 'Components/Organisms/UserProvider/useUser';
import DiscontinuedPlanSupportChat from 'Components/Organisms/DiscontinuedPlanSupportChat/DiscontinuedPlanSupportChat';
import { getProjectSelected } from 'Services/user';
import { trackError } from 'Utils/errorMonitoring';
import { PLANS_WITHOUT_SUPPORT } from 'Utils/constants';
import { ContextualHelpContext } from './ContextualHelpContext';
import { INTERCOM_APP_ID } from 'config';

export default function ContextualHelpProvider({ children }) {
    const { user } = useUser();
    const bootedUserIdRef = useRef(null);
    const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);

    const { data: project, isFetched } = useQuery({
        queryKey: ['projects', 'selected'],
        queryFn: getProjectSelected,
        onError: (error) => trackError(error),
        enabled: Boolean(user?.id),
    });

    const isPlanWithoutSupport = PLANS_WITHOUT_SUPPORT.includes(project?.subscription?.plan);
    const canBootIntercom = Boolean(user?.id) && isFetched && !isPlanWithoutSupport;

    useEffect(() => {
        if (!canBootIntercom) {
            if (bootedUserIdRef.current) {
                shutdownIntercom();
                bootedUserIdRef.current = null;
            }
            return;
        }

        if (bootedUserIdRef.current === user.id) return;

        if (bootedUserIdRef.current && bootedUserIdRef.current !== user.id) {
            shutdownIntercom();
        }

        const baseSettings = {
            app_id: INTERCOM_APP_ID,
            user_id: user.id,
            name: user.name,
            email: user.email,
            created_at: user.creation_date ? Math.floor(user.creation_date.getTime() / 1000) : undefined,
        };

        bootedUserIdRef.current = user.id;
        Intercom(baseSettings);
    }, [canBootIntercom, user?.id, user?.name, user?.email, user?.creation_date]);

    const openChatSupport = () => {
        if (isPlanWithoutSupport) {
            setIsSupportDialogOpen(true);
            return;
        }

        showIntercom();
    };

    return (
        <ContextualHelpContext.Provider
            value={{
                openChatSupport,
            }}
        >
            {children}

            {isPlanWithoutSupport && (
                <DiscontinuedPlanSupportChat
                    open={isSupportDialogOpen}
                    onOpen={() => setIsSupportDialogOpen(true)}
                    onClose={() => setIsSupportDialogOpen(false)}
                />
            )}
        </ContextualHelpContext.Provider>
    );
}

ContextualHelpProvider.propTypes = {
    children: PropTypes.node,
};
